import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { money } from "../lib/data";

export default function CheckoutResult({ kind }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useShop();
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);
  const cleared = useRef(false);

  useEffect(() => {
    if (kind !== "success" || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.status === "paid") {
          setResult(data);
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
      } catch {
        setFailed(true);
        return;
      }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setFailed(true);
    };
    poll();
  }, [kind, sessionId, clearCart]);

  if (kind === "cancel") {
    return (
      <div data-testid="checkout-cancel-page" className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-5 pt-24 text-center">
        <XCircle className="h-12 w-12 text-ll-red" />
        <h1 className="mt-6 font-display text-5xl tracking-wide">PAYMENT NOT COMPLETED</h1>
        <p className="mt-4 max-w-md text-sm text-ll-steel">
          No charge was made. Your cart is still saved — you can finish your order whenever you're ready.
        </p>
        <Link data-testid="cancel-back-to-shop" to="/shop"
              className="mt-8 bg-ll-red px-10 py-4 font-tech text-sm tracking-[0.25em] transition-colors duration-200 hover:bg-ll-redhover">
          BACK TO THE SHELF
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-success-page" className="mx-auto max-w-2xl px-5 pb-24 pt-32 md:pt-40">
      {!result && !failed && (
        <div className="flex flex-col items-center pt-16 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-ll-red" />
          <p data-testid="payment-confirming" className="mt-6 font-tech text-sm tracking-[0.2em] text-ll-steel">CONFIRMING YOUR PAYMENT…</p>
        </div>
      )}
      {failed && (
        <div className="flex flex-col items-center pt-16 text-center">
          <XCircle className="h-12 w-12 text-ll-red" />
          <h1 className="mt-6 font-display text-4xl tracking-wide">STILL CONFIRMING</h1>
          <p className="mt-4 text-sm text-ll-steel">
            Your payment is being processed. If you were charged, your confirmation email will arrive shortly. Questions? Use the contact page with the email you checked out with.
          </p>
        </div>
      )}
      {result && (
        <div data-testid="order-confirmation">
          <CheckCircle className="h-12 w-12 text-green-500" />
          <p className="mt-6 font-tech text-[11px] tracking-[0.3em] text-ll-red">ETERNEL ORDER</p>
          <h1 className="mt-2 font-display text-6xl tracking-wide">YOU'RE IN.</h1>
          <p className="mt-4 text-sm text-ll-steel">
            Order <span data-testid="confirmation-order-number" className="font-tech text-ll-bone">{result.order_number}</span> is confirmed.
            A confirmation email with your order-status link is on its way.
          </p>
          <div className="mt-8 border border-ll-line bg-ll-coal p-6">
            {result.items.map((i) => (
              <div key={i.slug || i.sku} className="flex items-center justify-between border-b border-ll-line py-3 last:border-0">
                <div>
                  <p className="text-sm font-semibold">{i.name || i.title}</p>
                  <p className="font-tech text-[11px] tracking-[0.15em] text-ll-steel">QTY {i.quantity}</p>
                </div>
                <p className="font-tech text-sm">{money(i.unit_price * i.quantity)}</p>
              </div>
            ))}
            <p className="mt-4 font-tech text-xs text-ll-steel">ESTIMATED SHIPPING: <span data-testid="confirmation-ship-window" className="text-ll-bone">{result.ship_window}</span></p>
            {result.amount_total && <p className="mt-1 font-tech text-xs text-ll-steel">TOTAL PAID: <span className="text-ll-bone">{money(result.amount_total)}</span> (incl. shipping & tax)</p>}
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link data-testid="confirmation-track-link" to={result.track_url}
                  className="bg-ll-red px-8 py-4 font-tech text-xs tracking-[0.25em] transition-colors duration-200 hover:bg-ll-redhover">
              VIEW ORDER STATUS
            </Link>
            <Link data-testid="confirmation-back-link" to="/"
                  className="border border-ll-bone px-8 py-4 font-tech text-xs tracking-[0.25em] transition-colors duration-200 hover:bg-ll-bone hover:text-ll-black">
              BACK HOME
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
